import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChange } from "../firebase/auth";
import { createUserFromAuthData } from "../firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let unsubscribe = null;
    let timeoutId = null;
    
    // OPTIMIZED: Set a timeout to stop blocking after 1 second (for new users)
    // This prevents infinite blocking if auth state change is slow
    timeoutId = setTimeout(() => {
      if (!authInitialized) {
        setAuthInitialized(true);
        setLoading(false);
      }
    }, 1000);
    
    try {
      unsubscribe = onAuthStateChange(async (user) => {
        setCurrentUser(user);
        
        // Only set loading to false after auth is fully initialized
        if (!authInitialized) {
          setAuthInitialized(true);
          setLoading(false);
          // Clear timeout since auth initialized
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        } else {
          // If already initialized, ensure loading is false
          setLoading(false);
        }

        // Automatically create user profile in Firestore if user is authenticated
        // OPTIMIZED: Don't block on this - do it in background for new users
        if (user) {
          // Don't await - let it run in background to avoid blocking
          createUserFromAuthData(user, {
            name: user.displayName || 'User',
            email: user.email,
            phone: '',
            displayName: user.displayName || 'User'
          }).catch(error => {
            console.error('❌ Failed to ensure user profile in Firestore:', error);
            // Don't throw error - user is still authenticated
          });
        }
      });
    } catch (error) {
      console.error('❌ Error setting up auth listener:', error);
      setLoading(false);
      setAuthInitialized(true);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [authInitialized]);

  const value = {
    currentUser,
    loading,
    authInitialized,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
